<script setup>
import { computed, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useHubBadges } from '../../composables/useHubBadges';
import { groupNavItems, visibleNavItems } from '../../routes/navigation';
import { useAuthStore } from '../../stores/auth';
import { goToSectionHome } from '../../composables/useUnsavedChanges';
import HubNavItem from './HubNavItem.vue';

defineProps({
  collapsed: { type: Boolean, default: false },
});

const emit = defineEmits(['navigate']);

const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();
const { badges, release } = useHubBadges();
onBeforeUnmount(release);

// A computed, not a ref filled once at setup. The old sidebar snapshotted its
// items on mount, so a role change — which the auth store refreshes in place
// precisely so nobody has to log out and back in — left the nav showing the
// previous role's items until a reload.
const groups = computed(() =>
  groupNavItems(visibleNavItems(authStore.userRole, authStore.capabilities, authStore.pages))
);

// The route says which section it belongs to. No path parsing, so a deep page
// (/recordings/12), a section owning more than one path and a page with a
// ?tab= all highlight correctly without anyone thinking about it.
const activeId = computed(() => route.meta?.navItem ?? null);

// An editor open in place (a sequence, a policy line) has no route of its own:
// a route change alone would leave it open.
const backToSectionHome = async (item) => {
  if (goToSectionHome(router, item.to)) return;
  if (route.fullPath !== item.to) await router.push(item.to);
  if (route.fullPath === item.to) window.scrollTo(0, 0);
};

const go = (item) => {
  emit('navigate');
  if (item.id === activeId.value) void backToSectionHome(item);
  else router.push(item.to);
};
</script>

<template>
  <nav class="hub-nav" aria-label="Hub sections">
    <div v-for="(group, index) in groups" :key="`group-${index}`" class="hub-nav__group">
      <HubNavItem
        v-for="item in group"
        :key="item.id"
        :item="item"
        :active="item.id === activeId"
        :collapsed="collapsed"
        :count="badges[item.badge] ?? 0"
        @select="go"
      />
      <div v-if="index < groups.length - 1" class="hub-nav__divider" />
    </div>
  </nav>
</template>

<style scoped>
.hub-nav {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
  width: 100%;
  padding: 4px 0;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.hub-nav::-webkit-scrollbar {
  display: none;
}

.hub-nav__group {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
}

.hub-nav__divider {
  height: 1px;
  margin: 8px 16px;
  background: var(--XL-M-Theme-sys-light-outline-variant, #cec3ce);
}
</style>
